import { useQuery } from "convex/react";
import { useMemo, ReactElement } from "react";
import type { FunctionReference } from "convex/server";
import { getColumnCount } from "./useTableColums";
import SkeletonTable from "@/components/table/skeleton-table";
import { TableColumn } from "@/components/table/data-table";

export interface TableSearchParams {
  search?: string;
  status?: string;
  page?: number;
  [key: string]: any;
}

export interface TableConfig<TData = any> {
  tableName: string;
  query: FunctionReference<"query">;
  title: string;
  description: string;
  defaultPageSize?: number;
  columns?: TableColumn[]; // Columnas para el skeleton
  buildQueryParams?: (
    currentPage: number,
    pageSize: number,
    searchParams: TableSearchParams,
  ) => Record<string, any>;
  buildPageUrl?: (
    basePath: string,
    page: number,
    searchParams: TableSearchParams,
  ) => string;
}

export interface TableDataResult<TData = any> {
  data: TData[] | undefined;
  totalCount: number;
  /** totalCount es una cota inferior (paginación por índice, ver index_paginate). */
  totalCountIsLowerBound: boolean;
  isLoading: boolean;
  isError: boolean;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  basePath: string;
  nextPageUrl: string | null;
  previousPageUrl: string | null;
  resetFiltersUrl: string;
  renderSkeleton: () => ReactElement;
  renderError: (message?: string) => ReactElement;
  navigationProps: {
    basePath: string;
    currentPage: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    nextPageUrl: string | null;
    previousPageUrl: string | null;
    resetFiltersUrl: string;
  };
}

export function useTableData<TData = any>(
  config: TableConfig<TData>,
  options: {
    currentPage: number;
    pageSize: number;
    basePath: string;
    searchParams?: TableSearchParams;
  },
): TableDataResult<TData> {
  const {
    tableName,
    query,
    title,
    description,
    defaultPageSize = 10,
    columns,
    buildQueryParams,
    buildPageUrl,
  } = config;

  const {
    currentPage,
    pageSize = defaultPageSize,
    basePath,
    searchParams = {},
  } = options;

  const queryParams = useMemo(() => {
    if (buildQueryParams) {
      return buildQueryParams(currentPage, pageSize, searchParams);
    }

    // Filtrar parametros de UI que no son parte del validador de Convex.
    const UI_PARAMS = new Set(["action", "targetMaxRows"]);
    const { page, ...restParams } = searchParams;
    const validSearchParams = Object.fromEntries(
      Object.entries(restParams).filter(([key]) => !UI_PARAMS.has(key)),
    );

    const params = {
      pageNumber: (currentPage || 1) - 1, // 0-indexed para paginateAndSort
      targetMaxRows:
        searchParams.targetMaxRows !== undefined
          ? typeof searchParams.targetMaxRows === "string"
            ? Number(searchParams.targetMaxRows.replace(/^"|"$/g, "")) ||
              pageSize
            : searchParams.targetMaxRows
          : pageSize,
      includeTotalCount: true,
      search: searchParams.search,
      status: searchParams.status,
      ...validSearchParams,
    };

    return params;
  }, [currentPage, pageSize, searchParams, buildQueryParams]);

  const queryResult = useQuery(query, queryParams);

  const buildUrl = useMemo(() => {
    if (buildPageUrl) {
      return (page: number) => buildPageUrl(basePath, page, searchParams);
    }

    return (page: number) => {
      const params = new URLSearchParams();
      if (searchParams.search) params.set("search", searchParams.search);
      if (searchParams.status) params.set("status", searchParams.status);

      Object.entries(searchParams).forEach(([key, value]) => {
        if (
          key !== "search" &&
          key !== "status" &&
          key !== "page" &&
          value !== undefined
        ) {
          params.set(key, String(value));
        }
      });

      // Incluir targetMaxRows si existe en searchParams
      if (searchParams.targetMaxRows !== undefined) {
        params.set("targetMaxRows", String(searchParams.targetMaxRows));
      }

      params.set("page", String(page));
      return `${basePath}?${params.toString()}`;
    };
  }, [basePath, searchParams, buildPageUrl]);

  const data = queryResult?.page as TData[] | undefined;
  const totalCount = queryResult?.totalCount ?? 0;
  const totalCountIsLowerBound = queryResult?.totalCountIsLowerBound === true;

  // Calcular pageSize actual basado en searchParams.targetMaxRows
  const currentPageSize =
    searchParams.targetMaxRows !== undefined
      ? typeof searchParams.targetMaxRows === "string"
        ? Number(searchParams.targetMaxRows.replace(/^"|"$/g, "")) || pageSize
        : searchParams.targetMaxRows
      : pageSize;

  const totalPages = Math.ceil(totalCount / currentPageSize);

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const nextPageUrl = hasNextPage ? buildUrl(currentPage + 1) : null;
  const previousPageUrl = hasPreviousPage ? buildUrl(currentPage - 1) : null;

  // Construir resetFiltersUrl con targetMaxRows si existe
  const resetFiltersUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (searchParams.targetMaxRows !== undefined) {
      params.set("targetMaxRows", String(searchParams.targetMaxRows));
    }
    params.set("page", "1");
    return `${basePath}?${params.toString()}`;
  }, [basePath, searchParams.targetMaxRows]);

  const isLoading = queryResult === undefined;
  const isError = queryResult === null;

  const renderSkeleton = () => {
    const columnCount = getColumnCount(tableName as any) || 8;

    return (
      <SkeletonTable
        columsNumber={columnCount}
        rowsNumber={pageSize}
        title={title}
        description={description}
        columns={columns}
      />
    );
  };

  const renderError = (message?: string) => (
    <div className="p-6 text-center">
      <p className="text-muted-foreground">
        {message || `No se pudieron cargar los datos`}
      </p>
    </div>
  );

  const navigationProps = {
    basePath,
    currentPage,
    pageSize: currentPageSize,
    totalCount,
    isLoading,
    nextPageUrl,
    previousPageUrl,
    resetFiltersUrl,
  };

  return {
    data,
    totalCount,
    totalCountIsLowerBound,
    isLoading,
    isError,
    currentPage,
    pageSize: currentPageSize,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    basePath,
    nextPageUrl,
    previousPageUrl,
    resetFiltersUrl,
    renderSkeleton,
    renderError,
    navigationProps,
  };
}
